import { useEffect, useState } from 'react';
import { WsClient } from './ws.js';

export interface WsEvent {
  id: number;
  ts: number;
  payload: { kind: string; payload: unknown };
}

const WS_URL = (() => {
  const apiBase = import.meta.env.VITE_API_BASE ?? '';
  if (apiBase.startsWith('https://')) return `${apiBase.replace(/^https/, 'wss')}/ws`;
  if (apiBase.startsWith('http://')) return `${apiBase.replace(/^http/, 'ws')}/ws`;
  // Same-origin fallback (dev mode)
  if (typeof window !== 'undefined') {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${window.location.host}/ws`;
  }
  return 'ws://localhost/ws';
})();

/**
 * Live event stream from the api-worker WebSocket. Maintains a rolling
 * buffer of the last N events so consumers can render them.
 */
export const useLiveEvents = (
  max = 50,
): {
  events: WsEvent[];
  state: 'connecting' | 'open' | 'closed' | 'error';
} => {
  const [events, setEvents] = useState<WsEvent[]>([]);
  const [state, setState] = useState<'connecting' | 'open' | 'closed' | 'error'>('connecting');

  useEffect(() => {
    const client = new WsClient({
      url: WS_URL,
      onStateChange: setState,
      onEvent: (evt) => {
        setEvents((prev) => {
          const next = [...prev, evt as WsEvent];
          return next.length > max ? next.slice(next.length - max) : next;
        });
      },
    });
    return () => {
      client.close();
    };
  }, [max]);

  return { events, state };
};
